<template>
	<span v-if="isVisible" class="l-count-badge no-tooltip-icon" :class="'tone-' + tone" :title="tooltip || null" data-testid="count-badge">
		<span aria-hidden="true">{{ displayText }}</span>
		<span class="l-count-badge-sr">{{ spokenText }}</span>
	</span>
</template>










<script setup>
/**
 * §8.4 CountBadge: a small number that says "this much is waiting", on a nav item or a tab.
 *
 * Hidden at zero, so the page that owns the count never has to guard it. Anything over `max`
 * reads "99+". The number is the visible text; `label` is what a screen reader hears instead of a
 * bare "3" ("3 days awaiting approval"). `tooltip` is the hover text for sighted users: say what is
 * counted and over what period, because a bare number never does. Never colour alone: the count
 * itself is the signal, the tone only says how urgent.
 *
 * Tones: `pending` (default, needs attention) and `neutral`.
 */

import { computed } from 'vue';

//-- Props
const props = defineProps({
	/** The number to show. Hidden when 0 (or not a positive number). */
	count: {
		type: Number,
		default: 0,
	},

	/** The largest number shown as-is; above it the badge reads "{max}+". */
	max: {
		type: Number,
		default: 99,
	},

	/** pending | neutral */
	tone: {
		type: String,
		default: 'pending',
	},

	/** The accessible text, e.g. "3 days awaiting approval". Defaults to the number. */
	label: {
		type: String,
		default: '',
	},

	/** What the number counts, shown on hover (a sentence, not a repeat of the number). Nothing is shown when blank. */
	tooltip: {
		type: String,
		default: '',
	},
});





//-- Computed Properties
const isVisible = computed(() => Number.isFinite(props.count) && props.count > 0);

const displayText = computed(() => props.count > props.max ? props.max + '+' : String(props.count));

const spokenText = computed(() => props.label || displayText.value);
</script>










<style scoped>
/* Named l-count-badge, never `badge`: legacy Bootstrap's global .badge would leak into it. */
.l-count-badge
{
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 18px;
	height: 18px;
	padding: 0 6px;
	border-radius: var(--r-pill);
	font-family: var(--font-ui);
	font-size: var(--text-micro);
	font-weight: 700;
	line-height: 1;
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
	cursor: default;
}

.tone-pending
{
	background: var(--status-pending-fill);
	color: var(--status-pending-text);
}

.tone-neutral
{
	background: var(--surface-sunken);
	color: var(--text-2);
	box-shadow: inset 0 0 0 1px var(--line);
}

/* Visually hidden, still read aloud. */
.l-count-badge-sr
{
	position: absolute;
	width: 1px;
	height: 1px;
	margin: -1px;
	padding: 0;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
}
</style>
